import type { AxiosInstance } from "axios";
import { API_ROUTES, ECOSYSTEM_API_PATH } from "@/constants/apiRoutes";
import {
  extractList,
  fetchPaginatedResource,
  isRecord,
  readNumber,
  readPaginationMarker,
  readString,
} from "@/services/apiResponseHelpers";
import { api } from "@/services/apiClient";
import type { ApiRequestOptions } from "@/services/iamApi";
import type {
  UiComponent,
  UiComponentReference,
  UiComponentScheme,
  UiComponentSpec,
  UiSidebarItem,
} from "@/types/ui";
import type { UiPropertySpec } from "@/types/uiPropertySpec";
import { normalizeUiComponentId } from "@/utils/uiComponentLinks";

const DEFAULT_FORM_METHOD = "POST";

/** The installation the visitor is working in, when they are in one. */
export interface InstallationRequest {
  // Its own API: what a rendered page asks for is answered there.
  api: AxiosInstance;
  // How the ecosystem is told which installation the request is about.
  headers: Record<string, string>;
}

// The interface builder's own resources — components and sidebar items — live
// in the ecosystem, which runs once on the central platform. An installation has
// no ecosystem of its own, so those requests never move.
//
// What a rendered page asks for is a different matter: its url is authored, and
// it usually addresses the platform the visitor is working in, which answers it
// directly. A page asking the ecosystem still reaches the central platform —
// carrying the name of the installation it is about, so the ecosystem fetches
// from there on the visitor's behalf.
function resolveRuntimeRequest(
  url: string,
  installation?: InstallationRequest,
): { client: AxiosInstance; headers?: Record<string, string> } {
  if (!installation) return { client: api };
  if (!url.trim().startsWith(ECOSYSTEM_API_PATH)) {
    return { client: installation.api };
  }

  return { client: api, headers: installation.headers };
}
const SIDEBAR_ITEMS_PAGE_LIMIT = 100;
const COMPONENTS_PAGE_LIMIT = 500;
// What a component list is asked for. The spec is left out: it depends on the
// kind alone and is read once per kind from the component specs, where on every
// component it outweighed the props several times over.
const COMPONENT_FIELDS = ["uuid", "project_id", "props"];
// A public page is only rendered, so it needs nothing but the props.
const PUBLIC_COMPONENT_FIELDS = ["uuid", "props"];
const DEFAULT_COMPONENT_KIND = "view";
const DEFAULT_DATA_SOURCE_METHOD = "GET";
const SIDEBAR_ITEM_KIND = "sidebar_item";
const DEFAULT_COMPONENT_SCHEME: UiComponentScheme = {
  kind: DEFAULT_COMPONENT_KIND,
  children: [],
};

interface CreateUiComponentInput {
  kind?: string;
  parentUuid?: string | null;
  props?: UiComponentScheme | Record<string, unknown>;
}

interface CreateUiComponentBatchInput extends CreateUiComponentInput {
  // A batch answers with a word rather than the components, so the caller
  // names every one of them itself.
  uuid: string;
}

interface CreateUiSidebarItemInput {
  name: string;
  description?: string;
  position: number;
  componentUuid?: string | null;
  // Everything else the item carries — its icon, group, permissions and
  // translations. A clone hands over the props of the item it copies, so the
  // copy arrives whole instead of an update following the create.
  props?: Record<string, unknown>;
}

interface FetchUiDataSourceInput {
  url: string;
  method?: string;
  page?: UiDataSourcePageRequest;
  // What the collection is asked to be filtered down to, field by field:
  // `{status: "ACTIVE"}` is sent as `?status=ACTIVE`, and the platform answers
  // with the records that match.
  filters?: Record<string, string>;
}

// One page of a paginated collection. The platform pages by marker, not by
// offset: a page is asked for by the marker the previous page returned, and the
// first page is asked for without one.
interface UiDataSourcePageRequest {
  limit: number;
  marker: string | null;
}

interface UiDataSourceResult {
  // The marker of the next page, or null when this page is the last one.
  nextMarker: string | null;
  payload: unknown;
}

interface SubmitUiFormInput {
  url: string;
  method?: string;
  body?: unknown;
}

interface UpdateUiComponentInput {
  parentUuid?: string | null;
  props?: UiComponentScheme | Record<string, unknown>;
}

interface UpdateUiSidebarItemInput {
  name?: string;
  description?: string;
  position?: number;
  componentUuid?: string | null;
  props?: Record<string, unknown>;
}

interface UiSidebarItemApiRecord {
  uuid?: unknown;
  name?: unknown;
  description?: unknown;
  position?: unknown;
  project_id?: unknown;
  component_uuid?: unknown;
  component?: unknown;
  props?: unknown;
  spec?: unknown;
  created_at?: unknown;
  updated_at?: unknown;
}

interface UiComponentApiRecord {
  uuid?: unknown;
  project_id?: unknown;
  parent_uuid?: unknown;
  component_type?: unknown;
  props?: unknown;
  spec?: unknown;
  created_at?: unknown;
  updated_at?: unknown;
}

interface NormalizedUiComponent extends UiComponent {
  position: number;
}

export async function fetchUiSidebarItems(
  options: ApiRequestOptions = {},
): Promise<UiSidebarItem[]> {
  return fetchPaginatedResource({
    pageLimit: SIDEBAR_ITEMS_PAGE_LIMIT,
    requestOptions: options,
    resourceUri: API_ROUTES.ecosystem.ui.sidebarItems,
    normalizePage: normalizeSidebarItemsResponse,
    sortItems: sortUiSidebarItems,
  });
}

export async function fetchUiComponents(
  options: ApiRequestOptions = {},
): Promise<UiComponent[]> {
  const components = await fetchPaginatedResource({
    pageLimit: COMPONENTS_PAGE_LIMIT,
    requestOptions: options,
    resourceUri: API_ROUTES.ecosystem.ui.components,
    query: { fields: COMPONENT_FIELDS },
    normalizePage: normalizeComponentsResponse,
    sortItems: sortUiComponents,
  });

  return sortUiComponents(attachComponentChildren(components));
}

/** What the props of every kind of component are, one spec per kind. */
export async function fetchUiComponentSpecs(
  options: ApiRequestOptions = {},
): Promise<UiComponentSpec[]> {
  const { data } = await api.get<unknown>(
    API_ROUTES.ecosystem.ui.componentSpecs,
    options,
  );

  return extractList(data).flatMap((value) => {
    if (!isRecord(value)) return [];

    const kind = readString(value.kind);
    const spec = readPropertySpec(value.spec);
    return kind && spec ? [{ uuid: kind, kind, spec }] : [];
  });
}

/**
 * The sidebar items of the pages an administrator marked with a public path.
 *
 * They are answered without a token, so a visitor who is not signed in reads
 * them: the item carries the path the page is served at, which is how a public
 * route resolves the component it renders.
 */
export async function fetchPublicUiSidebarItems(
  options: ApiRequestOptions = {},
): Promise<UiSidebarItem[]> {
  return fetchPaginatedResource({
    pageLimit: SIDEBAR_ITEMS_PAGE_LIMIT,
    requestOptions: options,
    resourceUri: API_ROUTES.ecosystem.ui.publicSidebarItems,
    normalizePage: normalizeSidebarItemsResponse,
    sortItems: sortUiSidebarItems,
  });
}

/** The components the public pages are built from, answered without a token. */
export async function fetchPublicUiComponents(
  options: ApiRequestOptions = {},
): Promise<UiComponent[]> {
  const components = await fetchPaginatedResource({
    pageLimit: COMPONENTS_PAGE_LIMIT,
    requestOptions: options,
    resourceUri: API_ROUTES.ecosystem.ui.publicComponents,
    query: { fields: PUBLIC_COMPONENT_FIELDS },
    normalizePage: normalizeComponentsResponse,
    sortItems: sortUiComponents,
  });

  return sortUiComponents(attachComponentChildren(components));
}

export async function createUiComponent(
  input: CreateUiComponentInput = {},
  options: ApiRequestOptions = {},
): Promise<UiComponent> {
  const { data } = await api.post<unknown>(
    API_ROUTES.ecosystem.ui.components,
    { props: toCreateComponentProps(input) },
    options,
  );
  return requireUiComponent(data);
}

/**
 * Writes several components in one request.
 *
 * A whole subtree -- cloning one -- used to cost a request per component, each
 * waiting for the uuid its children link to. The uuids are the caller's own
 * here, so the subtree is described in full up front and written at once; the
 * server answers that it wrote it rather than answering with the components,
 * which the caller already holds.
 */
export async function createUiComponents(
  inputs: readonly CreateUiComponentBatchInput[],
  options: ApiRequestOptions = {},
): Promise<void> {
  if (!inputs.length) return;

  await api.post<unknown>(
    API_ROUTES.ecosystem.ui.componentsBatch,
    {
      components: inputs.map((input) => ({
        uuid: input.uuid,
        props: toCreateComponentProps(input),
      })),
    },
    options,
  );
}

export async function createUiSidebarItem(
  input: CreateUiSidebarItemInput,
  options: ApiRequestOptions = {},
): Promise<UiSidebarItem> {
  const componentUuid = input.componentUuid;
  const { data } = await api.post<unknown>(
    API_ROUTES.ecosystem.ui.sidebarItems,
    {
      props: {
        ...(input.props ?? {}),
        kind: SIDEBAR_ITEM_KIND,
        name: input.name,
        description: input.description ?? "",
        position: input.position,
        ...(componentUuid ? { component_uuid: componentUuid } : {}),
      },
    },
    options,
  );
  return requireUiSidebarItem(data);
}

export async function updateUiComponent(
  uuid: string,
  input: UpdateUiComponentInput,
  options: ApiRequestOptions = {},
): Promise<UiComponent> {
  const payload: Record<string, unknown> = {};
  if (input.props !== undefined) {
    payload.props = toComponentPropsPayload(input.props);
  }
  if (input.parentUuid !== undefined) {
    const props = (payload.props as Record<string, unknown> | undefined) ?? {};
    props.parent_uuid = input.parentUuid;
    payload.props = props;
  }

  const { data } = await api.put<unknown>(
    getUiComponentResourceUri(uuid),
    payload,
    options,
  );
  return requireUiComponent(data);
}

export async function updateUiSidebarItem(
  uuid: string,
  input: UpdateUiSidebarItemInput,
  options: ApiRequestOptions = {},
): Promise<UiSidebarItem> {
  const payload: Record<string, unknown> = {};

  if (input.name !== undefined) payload.name = input.name;
  if (input.description !== undefined) payload.description = input.description;
  if (input.position !== undefined) payload.position = input.position;
  if (input.componentUuid !== undefined) {
    payload.component_uuid = input.componentUuid;
  }
  if (input.props !== undefined) payload.props = input.props;

  const { data } = await api.put<unknown>(
    getUiSidebarItemResourceUri(uuid),
    payload,
    options,
  );
  return requireUiSidebarItem(data);
}

export async function deleteUiSidebarItem(
  uuid: string,
  options: ApiRequestOptions = {},
): Promise<void> {
  await api.delete(getUiSidebarItemResourceUri(uuid), options);
}

/**
 * Removes several components in one request.
 *
 * Deleting a subtree -- a page, a group, an undone clone -- used to cost a
 * request per component. The server removes exactly the components named, in
 * one transaction: a uuid naming no component of the caller's project leaves
 * the whole tree standing, so a half-deleted page cannot be left behind.
 */
export async function deleteUiComponents(
  uuids: readonly string[],
  options: ApiRequestOptions = {},
): Promise<void> {
  if (!uuids.length) return;

  await api.post<unknown>(
    API_ROUTES.ecosystem.ui.componentsBatchDelete,
    { uuids: [...uuids] },
    options,
  );
}

export async function fetchUiDataSource(
  input: FetchUiDataSourceInput,
  options: ApiRequestOptions = {},
  installation?: InstallationRequest,
): Promise<UiDataSourceResult> {
  // The data source component loads its payload from a user-configured relative
  // URL and keeps it in memory; the request flows through the shared `api`
  // client so auth and token refresh apply. Domain data is NOT committed to
  // IndexedDB here — this is ephemeral, in-memory data owned by the rendered
  // component.
  const request = resolveRuntimeRequest(input.url, installation);
  let url = input.url;
  let filters = input.filters;
  // A configured restriction and the visitor's search are one expression.
  // Sending two q parameters would be rejected by the platform.
  if (filters?.q) {
    const [pathAndQuery, fragment] = url.split("#", 2);
    const queryIndex = pathAndQuery.indexOf("?");
    if (queryIndex >= 0) {
      const params = new URLSearchParams(pathAndQuery.slice(queryIndex + 1));
      const configured = params.getAll("q").filter((value) => value.trim());
      if (configured.length) {
        filters = { ...filters, q: [...configured, filters.q].map((value) => `(${value})`).join(" AND ") };
        params.delete("q");
        const query = params.toString();
        url = `${pathAndQuery.slice(0, queryIndex)}${query ? `?${query}` : ""}${fragment === undefined ? "" : `#${fragment}`}`;
      }
    }
  }
  const { data, headers } = await request.client.request<unknown>({
    ...options,
    ...(request.headers ? { headers: request.headers } : {}),
    method: input.method ?? DEFAULT_DATA_SOURCE_METHOD,
    url,
    ...(filters || input.page
      ? {
          params: {
            ...filters,
            ...(input.page ? buildPageParams(input.page) : {}),
          },
        }
      : {}),
  });
  // Whether another page exists is answered by the pagination header alone: the
  // collection endpoints report neither a total count nor a page count.
  return {
    nextMarker: readPaginationMarker(headers) ?? null,
    payload: data,
  };
}

function buildPageParams(
  page: UiDataSourcePageRequest,
): Record<string, number | string> {
  return {
    page_limit: page.limit,
    ...(page.marker ? { page_marker: page.marker } : {}),
  };
}

export async function submitUiForm(
  input: SubmitUiFormInput,
  options: ApiRequestOptions = {},
  installation?: InstallationRequest,
): Promise<unknown> {
  // A form component submits its resolved payload to a user-configured relative
  // URL. The request flows through the shared `api` client so auth and token
  // refresh apply, mirroring the data source loader. Nothing is committed to
  // IndexedDB — the response only drives the post-submit navigation.
  const request = resolveRuntimeRequest(input.url, installation);
  const { data } = await request.client.request<unknown>({
    ...options,
    ...(request.headers ? { headers: request.headers } : {}),
    method: input.method ?? DEFAULT_FORM_METHOD,
    url: input.url,
    data: input.body,
  });
  return data;
}

function normalizeSidebarItemsResponse(data: unknown): UiSidebarItem[] {
  return extractList(data)
    .map(normalizeSidebarItem)
    .filter((item): item is UiSidebarItem => item !== null);
}

function normalizeComponentsResponse(data: unknown): NormalizedUiComponent[] {
  return extractList(data)
    .map(normalizeComponent)
    .filter((component): component is NormalizedUiComponent => component !== null);
}

function normalizeSidebarItem(value: unknown): UiSidebarItem | null {
  if (!isRecord(value)) return null;

  const record = value as UiSidebarItemApiRecord;
  const props = readRecordProps(record.props);
  const uuid = readString(record.uuid);
  const name = readString(props.name) ?? readString(record.name);
  if (!uuid || !name) return null;

  const component = normalizeComponentReference(record.component);
  const componentUuid = readSidebarItemComponentUuid(
    props,
    record.component_uuid,
    record.component,
    component,
  );
  const spec = readPropertySpec(record.spec);

  return {
    uuid,
    name,
    description: readString(props.description) ?? readString(record.description) ?? "",
    position: readOptionalNumber(props.position)
      ?? readOptionalNumber(record.position)
      ?? 0,
    props,
    ...(spec ? { spec } : {}),
    projectId: readString(record.project_id),
    componentUuid,
    component,
    createdAt: readString(record.created_at) ?? undefined,
    updatedAt: readString(record.updated_at) ?? undefined,
  };
}

function normalizeComponentReference(value: unknown): UiComponentReference | null {
  if (typeof value === "string" && value) {
    const uuid = normalizeUiComponentId(value);
    return uuid ? { uuid } : null;
  }

  const component = normalizeComponent(value);
  if (!component) return null;

  return {
    uuid: component.uuid,
    name: component.name,
    description: component.description,
    projectId: component.projectId,
    parentUuid: component.parentUuid,
    componentType: component.componentType,
    uiScheme: component.uiScheme,
    createdAt: component.createdAt,
    updatedAt: component.updatedAt,
    props: component.props,
    ...(component.spec ? { spec: component.spec } : {}),
  };
}

function normalizeComponent(value: unknown): NormalizedUiComponent | null {
  if (!isRecord(value)) return null;

  const record = value as UiComponentApiRecord;
  const uuid = readString(record.uuid);
  if (!uuid) return null;

  const componentType = readString(record.component_type) ?? DEFAULT_COMPONENT_KIND;
  const props = normalizeComponentProps(record.props, componentType);
  const name = readString(props.name) ?? uuid;
  const description = readString(props.description) ?? "";
  const spec = readPropertySpec(record.spec);

  return {
    uuid,
    name,
    description,
    projectId: readString(record.project_id),
    parentUuid: normalizeUiComponentId(
      readString(props.parent_uuid) ?? readString(record.parent_uuid),
    ),
    componentType,
    props,
    ...(spec ? { spec } : {}),
    uiScheme: props,
    position: readNumber(props.position) ?? 0,
    createdAt: readString(record.created_at) ?? undefined,
    updatedAt: readString(record.updated_at) ?? undefined,
  };
}

function normalizeComponentProps(
  value: unknown,
  fallbackKind: string,
): UiComponentScheme {
  if (!isRecord(value)) {
    return {
      ...DEFAULT_COMPONENT_SCHEME,
      kind: fallbackKind || DEFAULT_COMPONENT_KIND,
    };
  }

  return {
    ...value,
    kind: readString(value.kind) ?? fallbackKind ?? DEFAULT_COMPONENT_KIND,
    children: Array.isArray(value.children) ? value.children : [],
  };
}

function attachComponentChildren(
  components: readonly NormalizedUiComponent[],
): UiComponent[] {
  const byParentUuid = new Map<string, NormalizedUiComponent[]>();
  const byUuid = new Map(components.map((component) => [component.uuid, component]));

  components.forEach((component) => {
    if (!component.parentUuid) return;

    const siblings = byParentUuid.get(component.parentUuid) ?? [];
    siblings.push(component);
    byParentUuid.set(component.parentUuid, siblings);
  });

  const buildScheme = (
    component: NormalizedUiComponent,
    visitedUuids: ReadonlySet<string>,
  ): UiComponentScheme | Record<string, unknown> => {
    if (visitedUuids.has(component.uuid)) return component.uiScheme;

    const nextVisitedUuids = new Set(visitedUuids);
    nextVisitedUuids.add(component.uuid);
    const children = sortUiComponents(byParentUuid.get(component.uuid) ?? [])
      .map((child) => createEmbeddedComponentScheme(
        child,
        buildScheme(child, nextVisitedUuids),
      ));

    return {
      ...component.uiScheme,
      children,
    };
  };

  return components.map((component) => {
    const uiScheme = buildScheme(component, new Set());
    return {
      ...component,
      uiScheme,
      parentUuid: byUuid.has(component.parentUuid ?? "")
        ? component.parentUuid
        : component.parentUuid,
    };
  });
}

function createEmbeddedComponentScheme(
  component: NormalizedUiComponent,
  scheme: UiComponentScheme | Record<string, unknown>,
): UiComponentScheme | Record<string, unknown> {
  return {
    ...scheme,
    parent_uuid: component.parentUuid,
    uuid: component.uuid,
  };
}

function readComponentUuid(
  rawComponentUuid: unknown,
  rawComponent: unknown,
  component: UiComponentReference | null,
): string | null {
  const componentUuid = normalizeUiComponentId(readString(rawComponentUuid));
  if (componentUuid) return componentUuid;

  if (typeof rawComponent === "string" && rawComponent) {
    return normalizeUiComponentId(rawComponent);
  }
  return component?.uuid ?? null;
}

function readSidebarItemComponentUuid(
  props: Record<string, unknown>,
  rawComponentUuid: unknown,
  rawComponent: unknown,
  component: UiComponentReference | null,
): string | null {
  const propertyComponentUuid = readString(props.component_uuid)
    ?? readString(props.componentUuid);
  const recordComponentUuid = readString(rawComponentUuid);
  return readComponentUuid(
    propertyComponentUuid
    ?? recordComponentUuid,
    rawComponent,
    component,
  );
}

function readRecordProps(value: unknown): Record<string, unknown> {
  if (!isRecord(value)) return {};
  return value;
}

function readPropertySpec(value: unknown): UiPropertySpec | undefined {
  return isRecord(value) ? value as UiPropertySpec : undefined;
}

function readOptionalNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string" || !value.trim()) return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function requireUiComponent(value: unknown): UiComponent {
  const component = normalizeComponent(value);
  if (!component) {
    throw new Error("Unable to normalize UI component response.");
  }
  return {
    ...component,
    uiScheme: {
      ...component.uiScheme,
      children: [],
    },
  };
}

function requireUiSidebarItem(value: unknown): UiSidebarItem {
  const item = normalizeSidebarItem(value);
  if (!item) {
    throw new Error("Unable to normalize UI sidebar item response.");
  }
  return item;
}

function getUiComponentResourceUri(componentUuid: string): string {
  return `${API_ROUTES.ecosystem.ui.components}${encodeURIComponent(componentUuid)}`;
}

function getUiSidebarItemResourceUri(sidebarItemUuid: string): string {
  return `${API_ROUTES.ecosystem.ui.sidebarItems}${encodeURIComponent(sidebarItemUuid)}`;
}

/**
 * A component created from scratch declares nothing but the `kind`
 * discriminator and the parent reference: the server fills in every other
 * property from its model defaults (name, dimensions, colors, padding, ...).
 * A caller that already knows what the component has to look like -- cloning
 * an existing one -- sends those properties here, so the component is stored
 * as it is meant to be from its first write.
 */
function toCreateComponentProps(
  input: CreateUiComponentInput,
): Record<string, unknown> {
  const props: Record<string, unknown> = input.props
    ? toComponentPropsPayload(input.props)
    : {};
  if (input.kind !== undefined) props.kind = input.kind;
  if (input.parentUuid !== undefined) props.parent_uuid = input.parentUuid;

  return props;
}

function toComponentPropsPayload(
  props: UiComponentScheme | Record<string, unknown>,
): Record<string, unknown> {
  const payload = { ...props };
  delete payload.children;
  return payload;
}

function sortUiSidebarItems(items: readonly UiSidebarItem[]): UiSidebarItem[] {
  return [...items].sort((a, b) => (
    a.position - b.position
    || a.name.localeCompare(b.name)
    || a.uuid.localeCompare(b.uuid)
  ));
}

function sortUiComponents<T extends UiComponent>(components: readonly T[]): T[] {
  return [...components].sort((a, b) => (
    readNumber(a.uiScheme.position) - readNumber(b.uiScheme.position)
    || a.name.localeCompare(b.name)
    || a.uuid.localeCompare(b.uuid)
  ));
}
